import { useId } from '@uifabric/react-hooks';
import { Persona, PersonaSize } from 'office-ui-fabric-react';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import { SrOnlyText } from '../SrOnlyText/SrOnlyText';
import { BaseCard } from '../BaseCard/BaseCard';
import { Body1Text, Body2Text } from '../Typography/Typography';

export interface IPeopleCardProps {
  /**
   * Person Name
   */
  title: string;

  /**
   * Link to the user's profile image on LinkedIn
   */
  profileImageUrl?: string;

  /**
   * Subtitle - typically `Position • Company`
   */
  subtitle?: string;

  /**
   * On Card Click
   */
  onCardClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;

  /**
   * Class Name
   */
  className?: string;
}

const StyledBaseCard = styled(BaseCard)`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 16px 8px;
`;

const InfoSection = styled.div`
  margin-top: 8px;
  margin-left: 16px;
  padding-bottom: 12px;
  overflow: hidden;
`;

const Title = styled.h3`
  margin: 0;
  color: #323130;
`;

const SubTitle = styled(Body1Text)`
  color: #505050;
`;

type IOptionalImgProps = {
  imageUrl?: string;
  imageInitials?: string;
};

export const PeopleCard: FunctionComponent<IPeopleCardProps> = ({ title, profileImageUrl, subtitle, onCardClick, className }) => {
  const titleId = useId('title');
  const descriptionId = useId('description');

  if (!title) return null;

  const optionalPersonaProps: IOptionalImgProps = {};
  if (profileImageUrl) {
    optionalPersonaProps.imageUrl = profileImageUrl;
  } else {
    optionalPersonaProps.imageInitials = title
      .split(/[^\w]+/)
      .map(part => part.slice(0, 1))
      .slice(0, 3)
      .join('');
  }

  return (
    <StyledBaseCard onCardClick={onCardClick} className={className} aria-labelledby={titleId} aria-describedby={descriptionId}>
      <Persona size={PersonaSize.size72} hidePersonaDetails={true} {...optionalPersonaProps} imageAlt={`${title} profile`} />

      <InfoSection>
        <Title className="highlight-on-card-hover">
          <Body2Text id={titleId}>{title}</Body2Text>
          <SrOnlyText id={descriptionId}>{`Opens in pop up. ${(subtitle || '').replace('•', '')}`}</SrOnlyText>
        </Title>

        <SubTitle block nowrap>
          {subtitle}
        </SubTitle>
      </InfoSection>
    </StyledBaseCard>
  );
};

export default PeopleCard;
